import React, { useState, useEffect } from 'react';
import { AdminSidebar } from './AdminDashboard';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, Building2, User, Mail, Phone, Palette, Globe, Instagram, Facebook, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'nuevo', label: 'Nuevo', color: 'bg-blue-500' },
  { value: 'en_progreso', label: 'En Progreso', color: 'bg-yellow-500' },
  { value: 'revision', label: 'En Revisión', color: 'bg-purple-500' },
  { value: 'completado', label: 'Completado', color: 'bg-green-500' },
];

export default function AdminOnboarding({ user }) {
  const [onboardings, setOnboardings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOnboarding, setSelectedOnboarding] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  useEffect(() => {
    fetchOnboardings();
  }, []);
  
  const fetchOnboardings = async () => {
    try {
      const response = await fetch(`${API}/api/onboarding`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setOnboardings(data);
      }
    } catch (error) {
      console.error('Error fetching onboardings:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const updateStatus = async (onboardingId, newStatus) => {
    try {
      const response = await fetch(`${API}/api/onboarding/${onboardingId}/status?status=${newStatus}`, {
        method: 'PATCH',
        credentials: 'include'
      });
      
      if (response.ok) {
        setOnboardings(onboardings.map(onb => 
          onb.onboarding_id === onboardingId ? { ...onb, status: newStatus } : onb
        ));
        toast.success('Estado actualizado');
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };
  
  const getStatusBadge = (status) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === status);
    return (
      <Badge className={`${statusOption?.color || 'bg-zinc-500'} text-white hover:opacity-80`}>
        {statusOption?.label || status}
      </Badge>
    );
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  const viewDetails = (onboarding) => {
    setSelectedOnboarding(onboarding);
    setShowDetails(true);
  };
  
  return (
    <div className="min-h-screen bg-[#050505]">
      <AdminSidebar user={user} />
      
      <main className="ml-64 p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" data-testid="onboarding-title">Onboarding</h1>
          <p className="text-zinc-500 mt-1">Gestiona los proyectos de clientes</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : onboardings.length === 0 ? (
          <div className="bg-[#0A0A0A] border border-white/5 p-12 text-center">
            <p className="text-zinc-500">No hay onboardings todavía</p>
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-white/5">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Negocio</TableHead>
                  <TableHead className="text-zinc-400">Contacto</TableHead>
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400">Fecha</TableHead>
                  <TableHead className="text-zinc-400">Estado</TableHead>
                  <TableHead className="text-zinc-400">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {onboardings.map((onb) => (
                  <TableRow key={onb.onboarding_id} className="border-white/5" data-testid={`onboarding-row-${onb.onboarding_id}`}>
                    <TableCell className="font-medium">{onb.business_name}</TableCell>
                    <TableCell className="text-zinc-400">{onb.contact_name}</TableCell>
                    <TableCell className="text-zinc-400">{onb.email}</TableCell>
                    <TableCell className="text-zinc-400">{formatDate(onb.created_at)}</TableCell>
                    <TableCell>{getStatusBadge(onb.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetails(onb)}
                          className="hover:bg-white/10"
                          data-testid={`view-btn-${onb.onboarding_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Select
                          value={onb.status}
                          onValueChange={(value) => updateStatus(onb.onboarding_id, value)}
                        >
                          <SelectTrigger className="w-32 bg-transparent border-white/10" data-testid={`status-select-${onb.onboarding_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0A0A0A] border-white/10">
                            {STATUS_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        
        {/* Details Dialog */}
        <Dialog open={showDetails} onOpenChange={setShowDetails}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Detalles del Proyecto</DialogTitle>
            </DialogHeader>
            
            {selectedOnboarding && (
              <div className="space-y-6 mt-4">
                {/* Business Info */}
                <div className="space-y-4">
                  <h3 className="text-sm font-mono uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Información del Negocio
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Nombre del Negocio</p>
                      <p className="font-medium">{selectedOnboarding.business_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Contacto</p>
                      <p className="font-medium">{selectedOnboarding.contact_name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Email</p>
                      <p className="font-medium">{selectedOnboarding.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Teléfono</p>
                      <p className="font-medium">{selectedOnboarding.phone}</p>
                    </div>
                  </div>
                </div>
                
                {/* Description */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500">Descripción del Negocio</p>
                  <p className="text-zinc-300 bg-zinc-900 p-4">{selectedOnboarding.business_description}</p>
                </div>
                
                {/* Services */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500">Servicios</p>
                  <p className="text-zinc-300 bg-zinc-900 p-4">{selectedOnboarding.services}</p>
                </div>
                
                {/* Target */}
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500">Público Objetivo</p>
                  <p className="text-zinc-300">{selectedOnboarding.target_audience}</p>
                </div>
                
                {/* Design Preferences */}
                <div className="space-y-4">
                  <h3 className="text-sm font-mono uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Preferencias de Diseño
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedOnboarding.preferred_colors && (
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Colores Preferidos</p>
                        <p>{selectedOnboarding.preferred_colors}</p>
                      </div>
                    )}
                    {selectedOnboarding.style_preferences && (
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Estilo</p>
                        <p>{selectedOnboarding.style_preferences}</p>
                      </div>
                    )}
                  </div>
                  {selectedOnboarding.reference_websites && (
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Sitios de Referencia</p>
                      <p className="text-zinc-300">{selectedOnboarding.reference_websites}</p>
                    </div>
                  )}
                </div>
                
                {/* Social Media */}
                <div className="space-y-4">
                  <h3 className="text-sm font-mono uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Redes Sociales
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedOnboarding.instagram && (
                      <div className="flex items-center gap-2">
                        <Instagram className="w-4 h-4 text-zinc-500" />
                        <span>{selectedOnboarding.instagram}</span>
                      </div>
                    )}
                    {selectedOnboarding.facebook && (
                      <div className="flex items-center gap-2">
                        <Facebook className="w-4 h-4 text-zinc-500" />
                        <span>{selectedOnboarding.facebook}</span>
                      </div>
                    )}
                    {selectedOnboarding.google_maps_link && (
                      <div className="flex items-center gap-2 col-span-2">
                        <MapPin className="w-4 h-4 text-zinc-500" />
                        <a href={selectedOnboarding.google_maps_link} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline truncate">
                          {selectedOnboarding.google_maps_link}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Notes */}
                {selectedOnboarding.additional_notes && (
                  <div className="space-y-2">
                    <p className="text-xs text-zinc-500">Notas Adicionales</p>
                    <p className="text-zinc-300 bg-zinc-900 p-4">{selectedOnboarding.additional_notes}</p>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex justify-end gap-4 pt-4 border-t border-white/10">
                  <a 
                    href={`https://wa.me/${selectedOnboarding.phone?.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="border-white/20 hover:bg-white hover:text-black">
                      Contactar por WhatsApp
                    </Button>
                  </a>
                  <a href={`mailto:${selectedOnboarding.email}`}>
                    <Button className="bg-white text-black hover:bg-zinc-200">
                      Enviar Email
                    </Button>
                  </a>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
